import { useCallback } from 'react';
import { Button, cn, Flex } from '@kognitos/lattice';

export interface IRainFollowUpCardProps {
  followUpId: string;
  suggestions: string[];
  isSubmitting?: boolean;
  onSend?: (text: string) => void;
  className?: string;
}

/**
 * Renders follow-up suggestions as a horizontal group of suggestion pills
 * (the design-system "Button Group" widget). Clicking a pill sends it as a
 * user message; the parent then retires the whole card, so there is no
 * answered/disabled state to render here.
 */
export function RainFollowUpCard({
  followUpId,
  suggestions,
  isSubmitting = false,
  onSend,
  className,
}: IRainFollowUpCardProps) {
  const handleSuggestionClick = useCallback(
    (suggestion: string) => {
      if (isSubmitting || !onSend) return;
      onSend(suggestion);
    },
    [isSubmitting, onSend],
  );

  return (
    <Flex
      direction="row"
      wrap="wrap"
      gap={2}
      className={cn('w-full', className)}
      data-testid="rain-follow-up-card"
      data-follow-up-id={followUpId}
    >
      {suggestions.map(suggestion => (
        <Button
          key={suggestion}
          variant="outline"
          disabled={isSubmitting}
          onClick={() => handleSuggestionClick(suggestion)}
          className="h-auto min-w-0 max-w-full whitespace-normal py-2 text-left"
          data-testid="rain-follow-up-suggestion"
        >
          <span className="w-full min-w-0 break-words">{suggestion}</span>
        </Button>
      ))}
    </Flex>
  );
}
